import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { AppShellProvider } from "@/components/AppShell";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Quotes from "@/pages/Quotes";
import QuoteForm from "@/pages/QuoteForm";
import QuoteDetail from "@/pages/QuoteDetail";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import Receipts from "@/pages/Receipts";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import PublicQuote from "@/pages/PublicQuote";
import PublicInvoice from "@/pages/PublicInvoice";
import JobReport, { PublicJobReport } from "@/pages/JobReport";
import Onboarding from "@/pages/Onboarding";
import Activate from "@/pages/Activate";
import Support from "@/pages/Support";
import Restricted from "@/pages/Restricted";

const RESTRICTED = ["awaiting_payment", "suspended", "cancelled"];

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500 text-sm" data-testid="auth-loading">Loading...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (RESTRICTED.includes(user.account_status)) return <Restricted />;
  if ((user.account_status === "onboarding" || user.setup_required) && window.location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <AppShellProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/q/:token" element={<PublicQuote />} />
          <Route path="/i/:token" element={<PublicInvoice />} />
          <Route path="/r/:token" element={<PublicJobReport />} />
          <Route path="/activate/:token" element={<Activate />} />
          <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/quotes" element={<Quotes />} />
            <Route path="/quotes/new" element={<QuoteForm />} />
            <Route path="/quotes/:id" element={<QuoteDetail />} />
            <Route path="/quotes/:id/edit" element={<QuoteForm />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/jobs/:id/report" element={<JobReport />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/invoices/:id" element={<InvoiceDetail />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/receipts" element={<Receipts />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/support" element={<Support />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AppShellProvider>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}
